import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [cartCount, setCartCount] = useState(2);
  const [notifications, setNotifications] = useState(3);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { label: 'Browse Tours', path: '/homepage', icon: 'Compass' },
    { label: 'Search Results', path: '/search-results', icon: 'Search' },
    { label: 'My Dashboard', path: '/user-dashboard', icon: 'User' },
    { label: 'My Bookings', path: '/booking-flow', icon: 'Calendar' },
  ];

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleCartClick = () => {
    navigate('/payment-checkout');
  };

  const handleProfileClick = () => {
    navigate('/profile-management');
  };

  const handleAuthClick = () => {
    navigate('/authentication');
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-card border-b border-border' 
            : 'bg-white'
        }`}
      >
        <div className="w-full px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/homepage')}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
              >
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                  <Icon name="Compass" size={20} color="white" className="lg:w-6 lg:h-6" />
                </div>
                <span className="text-xl lg:text-2xl font-semibold text-primary">
                  TourEase
                </span>
              </button>
            </div>

            {/* Desktop Search */}
            <div className="hidden md:flex flex-1 max-w-2xl mx-8">
              <form onSubmit={handleSearch} className="w-full relative">
                <div className={`relative transition-all duration-200 ${
                  isSearchFocused ? 'transform scale-105' : ''
                }`}>
                  <Input
                    type="search"
                    placeholder="Search destinations, tours, activities..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    className="w-full pl-12 pr-4 py-3 rounded-full border-2 border-muted focus:border-primary transition-colors"
                  />
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                  />
                </div>
              </form>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-muted ${
                    location?.pathname === item?.path
                      ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary'
                  }`}
                >
                  {item?.label}
                </button>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-2">
              {/* Cart */}
              <button
                onClick={handleCartClick}
                className="relative p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="ShoppingCart" size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Notifications */}
              <button className="relative p-2 rounded-lg hover:bg-muted transition-colors">
                <Icon name="Bell" size={20} />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>

              {/* Profile */}
              <button
                onClick={handleProfileClick}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="User" size={20} />
              </button>

              {/* Auth */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAuthClick}
                iconName="LogIn"
                iconPosition="left"
              >
                Sign In
              </Button>
            </div>

            {/* Mobile Actions */}
            <div className="flex md:hidden items-center space-x-2">
              {/* Mobile Search */}
              <button
                onClick={() => navigate('/search-results')}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="Search" size={20} />
              </button>

              {/* Mobile Cart */}
              <button
                onClick={handleCartClick}
                className="relative p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="ShoppingCart" size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-border">
            <div className="px-4 py-4 space-y-2">
              {/* Mobile Search Form */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Input
                    type="search"
                    placeholder="Search destinations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2"
                  />
                  <Icon 
                    name="Search" 
                    size={18} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                  />
                </div>
              </form>

              {/* Mobile Navigation */}
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    location?.pathname === item?.path
                      ? 'text-primary bg-primary/10' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span className="font-medium">{item?.label}</span>
                </button>
              ))}

              {/* Mobile Profile & Auth */}
              <div className="pt-4 border-t border-border space-y-2">
                <button
                  onClick={handleProfileClick}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left hover:bg-muted transition-colors"
                >
                  <Icon name="User" size={18} />
                  <span className="font-medium">Profile</span>
                </button>
                
                <button
                  onClick={handleAuthClick}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left hover:bg-muted transition-colors"
                >
                  <Icon name="LogIn" size={18} />
                  <span className="font-medium">Sign In</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
      {/* Header Spacer */}
      <div className="h-16 lg:h-20" />
    </>
  );
};

export default Header;